import type * as pf from '@amirada/lib-shared'
import { PfCmdType } from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import { useAppIndexStore } from '@base/stores/indexStore'
import { usePfinderAPI } from '@base/composables/pfinderAPI'
import type { ECarts } from '@base/utils/constants'
import { def_localCartPrefix, ElocalStorageType } from '@base/utils/constants'
import { ut_modifyCountry, ut_returnNewRoute, ut_setLocalStorage } from '@base/utils/utils'

export const useCartStore = defineStore('cartStore', () => {
  const state: tp.ICartState = reactive({})

  const indexStore = useAppIndexStore()
  const pfinderAPI = usePfinderAPI()
  const appConfig = useAppConfig()

  /*
   * Carts that belong to this tab and never to the origin — see `tabLocalCarts` in app.config.
   *
   * Read as plain strings: the base declares the list as `string[]`, a client narrows it to its own
   * ECarts values, and this is asked with a key of the cart state. Comparing names is what is meant,
   * and the cast says so rather than pushing the narrowing through every caller.
   */
  function isTabLocalCart(cartName: string): boolean {
    return ((appConfig.tabLocalCarts || []) as string[]).includes(cartName)
  }

  /*
   * Replace complete state with new data from e.g. localStorage.
   *
   * ⚑ Tab-local carts are left ALONE, and skipping them in the copy below is not enough — the line
   * that empties every cart first would clear them just as thoroughly. This runs on every storage
   * event from another tab, so without the guard a cart that is never stored is still wiped by any
   * other tab that writes (measured 31.08.2026: a second open tab emptied the running price list).
   */
  function setWholeCart(carts: tp.ICartState): void {
    Object.keys(state).forEach((key) => {
      if (isTabLocalCart(key)) { return }
      state[key as keyof tp.ICartState] = []
    })
    if (carts) {
      for (const key in carts) {
        if (isTabLocalCart(key)) { continue }
        state[key as keyof tp.ICartState] = carts[key as keyof tp.ICartState]
      }
    }
  }

  // Update cart items from server
  async function setUpdateCart(cartName: ECarts): Promise<void> {
    const cart: pf.CartItem[] | undefined = state[cartName]
    if (cart && cart.length > 0) {
      state[cartName] = await updateCartItemsInfo(cart)
    }
    updateLocalStorage()
  }

  /* ### #15 — Async Cart ohne AbortController
  - **Datei:** `base/app/stores/cartStore.ts`, Zeilen 28–34
  - **Severity:** MEDIUM | **Aufwand:** 1 h

  **Problem:** Navigation weg von der Seite während `updateCartItemsInfo` läuft → State-Update einer "toten" Komponente; mögliche Zustandskorruption.

  **Fix:** `AbortController` übergeben und in `onUnmounted` / Store-Cleanup aufrufen. */

  // Empty cart by name
  function setEmptyCart(cartName: ECarts): void {
    state[cartName] = []
    updateLocalStorage()
  }

  // Replace complete state with new data from e.g. localStorage
  function setCart(cartName: ECarts, cart: pf.CartItem[]): void {
    state[cartName] = cart
  }

  // Remove item by namePath, nodeName or position
  function setRemoveItemFromCart(cartName: ECarts, namePath?: string, nodeName?: string, position?: number): void {
    if (!state[cartName]) { return }
    if (namePath || nodeName) {
      position = state[cartName].findIndex(item => namePath ? item.namePath === namePath : item.nodeName === nodeName)
    }
    if (position !== undefined && position > -1) {
      state[cartName].splice(position, 1)
    }
    updateLocalStorage()
  }

  function setUpdateAmount(cartName: ECarts, amount: number, namePath?: string, position?: number): void {
    if (!state[cartName]) { return }
    if (namePath) {
      position = state[cartName].findIndex(item => item.namePath === namePath)
    }
    if (position !== undefined && position > -1) {
      const item: pf.CartItem | undefined = state[cartName][position]
      if (item) {
        item.amount = amount
      }
    }
    updateLocalStorage()
  }

  function setUpdateUserDefined(cartName: ECarts, namePath: string, userDefined: any, key: string): void {
    if (!state[cartName]) { return }
    const position = state[cartName].findIndex(item => item.namePath === namePath)
    if (position > -1 && state[cartName][position]) {
      if (!state[cartName][position].userDefined) { { state[cartName][position].userDefined = {} } }
      state[cartName][position].userDefined[key] = userDefined
    }
    updateLocalStorage()
  }

  // Add single item to cart. first update from server and then add
  async function setAddItemToCart(cartName: ECarts, namePath: string, amount?: number, position?: number) {
    // Only names item + conf and amount needed everything else fetched from server
    const cartData: pf.CartItem = {
      namePath,
    }
    const updatedDataAr: pf.CartItem[] = await updateCartItemsInfo([cartData])
    if (updatedDataAr[0]) {
      addCartItem(cartName, updatedDataAr[0], amount, position)
    }
    updateLocalStorage()
  }

  async function setAddListToCart(cartName: ECarts, itemAr: pf.CartItem[], position?: number) {
    if (!state[cartName]) { state[cartName] = [] }
    const updatedDataAr: pf.CartItem[] = await updateCartItemsInfo(itemAr)
    for (const item of updatedDataAr) {
      if (!(state[cartName].some(i => i.namePath === item.namePath))) {
        addCartItem(cartName, item, undefined, position)
      }
    }
    updateLocalStorage()
  }

  async function setRemoveListFromCart(cartName: ECarts, itemAr: pf.CartItem[]) {
    if (!state[cartName]) { return }
    for (const item of itemAr) {
      const position = state[cartName].findIndex(i => i.namePath === item.namePath)
      if (position > -1) {
        state[cartName].splice(position, 1)
      }
    }
    updateLocalStorage()
  }

  // Sort cart by key
  function setSortCartByKey(cartName: ECarts, key: keyof pf.CartItem, order: 'asc' | 'desc'): void {
    if (!state[cartName]) { return }
    if (order === 'asc') {
      state[cartName].sort((a, b) => ((a[key] || '') > (b[key] || '')) ? 1 : -1)
    }
    else {
      state[cartName].sort((a, b) => ((a[key] || '') > (b[key] || '')) ? -1 : 1)
    }
    updateLocalStorage()
  }

  function setResortItemByPos(cartName: ECarts, from: number, to: number): void {
    if (!state[cartName]) { return }
    if (!state[cartName][from]) { return }
    const item: pf.CartItem = state[cartName][from]
    state[cartName].splice(from, 1)
    state[cartName].splice(to, 0, item)
    updateLocalStorage()
  }

  // Return cart
  function getCart(cartName: ECarts): pf.CartItem[] {
    return state[cartName] ? state[cartName] : []
  }

  // Check if item is in cart
  function getIsInCart(cartName: ECarts, namePath: string, nodeName: string): boolean {
    if (!state[cartName]) { return false }
    if (namePath) {
      return state[cartName].some(item => item.namePath === namePath)
    }
    else if (nodeName) {
      return state[cartName].some(item => item.namePath.endsWith(nodeName))
    }
    else {
      return false
    }
  }

  // Get cart count
  function getCartCount(cartName: ECarts): number {
    if (!state[cartName]) { return 0 }
    return state[cartName].length
  }

  // Update cart items (1 ... x) from server
  async function updateCartItemsInfo(itemAr: pf.CartItem[]): Promise<pf.CartItem[]> {
    const fetchStep: number = 1000
    // Local (non-DB) items are self-contained — never fetch them; only real items go to the server.
    const realItems: pf.CartItem[] = itemAr.filter(item => !item.namePath.startsWith(def_localCartPrefix))
    const fetched: pf.CartItem[] = []

    for (let i: number = 0; i < realItems.length; i += fetchStep) {
      const partCart: pf.CartItem[] = realItems.slice(i, i + fetchStep)
      const args: pf.PfCmd<PfCmdType.CART>['arg'] = {
        data: stripCart(partCart, false),
      }
      const data: tp.IFetchData = {
        paramData: JSON.parse(JSON.stringify(indexStore.state.paramData)),
        usePost: true,
      }
      // Wilo specific historic country always use non-historic for cart
      if (data.paramData) {
        data.paramData.rcco = ut_modifyCountry(data.paramData?.rcco || '')
      }

      const partRes = await pfinderAPI.fetchData(PfCmdType.CART, args, data)

      if (partRes && partRes.result) {
        fetched.push(...partRes.result)
      }
    }

    // Re-merge in original order: local items untouched, real items replaced by their fetched data
    // (real items missing from the response are dropped, as before).
    //
    // ⚑ One QUEUE per namePath, taken in order — not one entry per namePath. A cart may hold the same
    // object twice as two separate rows (the wilo price list does, 24.09.2026), and the server answers
    // each row on its own, with its own userDefined. A plain map kept only the last answer and handed
    // that ONE object to every row of the path: the rows then shared their price tables, and changing
    // one changed all. The server keeps the order of the rows it resolves, so the n-th answer for a
    // path belongs to the n-th row with it. For a cart without duplicates this is exactly the old map.
    const byNamePath: Map<string, pf.CartItem[]> = new Map()
    for (const f of fetched) {
      const queue: pf.CartItem[] | undefined = byNamePath.get(f.namePath)
      if (queue) { queue.push(f) }
      else { byNamePath.set(f.namePath, [f]) }
    }
    const res: pf.CartItem[] = []
    for (const item of itemAr) {
      if (item.namePath.startsWith(def_localCartPrefix)) {
        res.push(item)
      }
      else {
        const fetchedItem: pf.CartItem | undefined = byNamePath.get(item.namePath)?.shift()
        if (fetchedItem) { res.push(fetchedItem) }
      }
    }
    return res
  }

  // Add item to state cart
  function addCartItem(cartName: ECarts, item: pf.CartItem, amount?: number, position?: number): void {
    if (!state[cartName]) { state[cartName] = [] }
    let bfound: boolean = false
    if (amount) {
      const index: number = state[cartName].findIndex(i => (i.namePath === item.namePath))
      if (index > -1 && state[cartName][index]) {
        const actAmount: number = state[cartName][index]?.amount || 0
        state[cartName][index].amount = actAmount + amount
        bfound = true
      }
      else {
        item.amount = amount
      }
    }
    if (!bfound) {
      // Same range test as setInsertItem. It used to read `if (!position || position === -1)`,
      // which made index 0 falsy and therefore indistinguishable from "no position given" —
      // inserting at the FRONT silently appended instead. -1 is the append sentinel (see
      // ut_wilo_findPositionForCart), 0 is a real index and the two callers that return it mean
      // exactly that: a config belongs at the first place.
      if (position === undefined || position < 0 || position > state[cartName].length) {
        state[cartName].push(item)
      }
      else {
        state[cartName].splice(position, 0, item)
      }
    }
  }

  // Insert a pre-built item at a position without fetching — for local (non-DB) content items.
  function setInsertItem(cartName: ECarts, item: pf.CartItem, position?: number): void {
    if (!state[cartName]) { state[cartName] = [] }
    if (position === undefined || position < 0 || position > state[cartName].length) {
      state[cartName].push(item)
    }
    else {
      state[cartName].splice(position, 0, item)
    }
    updateLocalStorage()
  }

  // Save state to localStorage
  function updateLocalStorage(): void {
    if (appConfig.useLocalStorage) {
      const strippedState: tp.ICartState = {}
      for (const key in state) {
        // Left out entirely rather than written empty: this cart is not stored, and an entry saying
        // "empty" would be a statement about it that nobody made. Leaving the key out also drops
        // whatever an older version of the app had written there, on the first write.
        if (isTabLocalCart(key)) { continue }
        strippedState[key as keyof tp.ICartState] = stripCart(state[key as keyof tp.ICartState] || [], false)
      }
      ut_setLocalStorage(ElocalStorageType.ls_shoppingcart, JSON.stringify(strippedState))
    }
  }

  function stripCart(cart: pf.CartItem[], deep: boolean): pf.CartItem[] {
    const res: pf.CartItem[] = []
    for (const item of cart) {
      if (item.namePath) {
        const cartItem: pf.CartItem = {
          namePath: item.namePath,
        }
        if (!deep) {
          cartItem.nodeType = item.nodeType
          cartItem.amount = item.amount
          cartItem.userDefined = item.userDefined
        }
        res.push(cartItem)
      }
    }
    return res
  }

  function linkPath(path: string): tp.IParamRouter {
    const indexStore = useAppIndexStore()
    const queryAddType: tp.IQueryAddType = {
      noPageParams: true,
      noGlobalParams: false,
      noPsx: true,
      noHash: true,
      noCustom: true,
      noTab: true,
    }
    return ut_returnNewRoute(indexStore.getCountryLangPath + '/' + path, {}, '', queryAddType)
  }

  return {
    state,
    setUpdateCart,
    setAddItemToCart,
    setUpdateAmount,
    setWholeCart,
    setEmptyCart,
    setCart,
    setRemoveItemFromCart,
    setAddListToCart,
    setRemoveListFromCart,
    setResortItemByPos,
    setSortCartByKey,
    getCart,
    getIsInCart,
    getCartCount,
    linkPath,
    stripCart,
    setUpdateUserDefined,
    setInsertItem,
    // Exported for a client that assembles its own rows (wilo price list: the same series may stand
    // twice, which setAddListToCart deliberately prevents). Reads nothing from the state, writes nothing.
    updateCartItemsInfo,
  }
})
